// No Mixpanel event may carry a person's data or a secret. The payloads the shop builds pick ids,
// enums and amounts; what can carry either is a URL the SDK attaches to every event by itself
// ($current_url, the referrers), and the shop's own URLs carry both at times:
//   - `?auth_grant=nhas_user_token&auth_token=…`, a customer login token, until the login resolves
//     (NhasUserTokenAuthenticator);
//   - Stripe's `payment_intent_client_secret` / `setup_intent_client_secret` and Adyen's
//     `redirectResult` on the payment return URL, where complete_purchase fires;
//   - the gift-card recipient's `email`, an `order_draft_uuid`, an order `uuid`.
// A referrer can carry whatever another site puts in its URLs. A denylist cannot keep up with that, so
// the query is ALLOWLISTED: every parameter is dropped except the keys below. The fragment is dropped
// too: the app never reads it (BrowserRouter), and it is where implicit-grant tokens travel.

/**
 * The only query parameters a URL keeps. Each is a campaign/click id or an enum, never a person's data
 * or a credential. Matched exactly (after decoding the key).
 */
const SAFE_QUERY_KEYS = new Set([
  // Campaign attribution. Mixpanel also reads these into its own utm_* properties.
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  // The id of a restaurant's FoodAmigos tracking link: TrackingProvider saves it next to the UTMs as
  // the order's attribution.
  'utm_tracking_link_id',
  // Ad click ids (Google, Meta), saved by TrackingProvider for attribution.
  'gclid',
  'fbclid',
  // The payment-return outcome (succeeded / failed …) and the order type (gift_card) on the return URL.
  'redirect_status',
  'order_type',
  // `true` on every URL the native wrapper app loads (MobileAppProvider): the only thing on a client
  // event that tells app traffic from web traffic.
  'mobile_app',
]);

/** The URL-valued properties the Mixpanel SDK adds to events by itself. */
const URL_PROPERTIES = ['$current_url', '$referrer', '$initial_referrer', 'current_url_search'];

/**
 * Properties the SDK derives from a URL's query that are not URLs themselves, removed with the SDK's
 * `property_blacklist`: mp_keyword is the search query of a search-engine referrer.
 */
export const URL_DERIVED_PROPERTY_BLACKLIST = ['mp_keyword'];

const queryKey = (pair: string): null | string => {
  try {
    return decodeURIComponent(pair.split('=')[0].replace(/\+/g, ' '));
  } catch {
    // A malformed escape is not an allowlisted key.
    return null;
  }
};

/**
 * `url` without its fragment and without every query parameter that is not allowlisted. Returned
 * unchanged (byte for byte) when there is nothing to remove, so ordinary URLs keep their exact encoding.
 * Also takes a bare query (`?a=b`, the SDK's current_url_search).
 */
export function scrubUrl(url: string): string {
  const hashStart = url.indexOf('#');
  const withoutHash = hashStart === -1 ? url : url.slice(0, hashStart);
  const queryStart = withoutHash.indexOf('?');

  if (queryStart === -1) {
    return withoutHash;
  }

  const pairs = withoutHash.slice(queryStart + 1).split('&');
  const kept = pairs.filter((pair) => SAFE_QUERY_KEYS.has(queryKey(pair)));

  if (kept.length === pairs.length) {
    return withoutHash;
  }

  const base = withoutHash.slice(0, queryStart);

  return kept.length ? `${base}?${kept.join('&')}` : base;
}

/** Scrubs the URL properties of an event's property bag in place, and returns it. */
export function scrubUrlProperties<T extends Record<string, any>>(properties: T): T {
  URL_PROPERTIES.forEach((key) => {
    if (typeof properties[key] === 'string') {
      (properties as Record<string, any>)[key] = scrubUrl(properties[key]);
    }
  });

  return properties;
}

/** The part of a Mixpanel instance the first-touch referrer lives in. */
export interface MixpanelReferrerStore {
  get_property: (name: string) => unknown;
  register: (props: Record<string, unknown>) => void;
}

/**
 * The SDK keeps the first-touch referrer (`$initial_referrer`) raw in its persistence, sends it on every
 * event as a super property and once to the people profile (on load). This rewrites it scrubbed before
 * the SDK reads it. When it is not recorded yet, the SDK is about to record `document.referrer` (or
 * `$direct`) with a register_once, so the scrubbed version of that is registered first and the SDK's
 * register_once leaves it alone.
 */
export function sanitizeInitialReferrer(mp: MixpanelReferrerStore): void {
  const current = mp.get_property('$initial_referrer');
  const source = typeof current === 'string' && current !== '' ? current : document.referrer || '$direct';
  const scrubbed = scrubUrl(source);

  if (scrubbed !== current) {
    mp.register({ $initial_referrer: scrubbed });
  }
}

/**
 * Mixpanel `before_track` hook. It runs inside track() before the SDK merges in its own properties and
 * QUEUES the event, which is why the scrubbing has to happen here and not only in before_send_events:
 * the SDK keeps queued events in localStorage and, when a tab closed before they were confirmed sent,
 * re-sends them from a later page load with the before_send hooks skipped ("orphaned" items). An event
 * property overrides the SDK's own of the same name, so the scrubbed URLs are what gets queued.
 */
export function scrubUrlsBeforeTrack(mp: MixpanelReferrerStore) {
  return (eventName: string, properties: Record<string, any>): [string, Record<string, any>] => {
    sanitizeInitialReferrer(mp);

    const scrubbed = scrubUrlProperties({ ...properties });

    scrubbed.$current_url = scrubUrl(scrubbed.$current_url ?? window.location.href);

    if (document.referrer) {
      scrubbed.$referrer = scrubUrl(scrubbed.$referrer ?? document.referrer);
    }

    return [eventName, scrubbed];
  };
}

interface MixpanelEventPayload {
  event: string;
  properties: Record<string, any>;
}

/**
 * Mixpanel `before_send_events` hook: the last pass over an event's URL properties just before it is
 * sent. Always returns the payload — returning nothing would make the SDK drop the event.
 */
export function scrubUrlsBeforeSend<T extends MixpanelEventPayload>(payload: T): T {
  if (payload?.properties) {
    scrubUrlProperties(payload.properties);
  }

  return payload;
}
